import type { ReactNode } from 'react';
import { memo, useMemo, useRef } from 'react';
import { Tabs } from './Tabs';
import { WorkspaceConnectionBanner } from '@/features/workspace/connection-banner/components/WorkspaceConnectionBanner';
import { useWorkspaceStore } from '../store/workspaceStore';
import { getWorkspaceComponent } from '../registry/workspaceRegistry';
import type { WorkspaceType } from '../types/workspaceTypes';
import {
  useAppStore,
  MIN_VERTICAL_TABS_WIDTH,
  MAX_VERTICAL_TABS_WIDTH,
  DEFAULT_VERTICAL_TABS_WIDTH,
} from '../../../../store/appStore';
import { useSplitterDrag } from '../../../../layout/hooks/useSplitterDrag';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';

/** Keyboard resize step for the vertical-tabs column seam, in pixels (Shift = 4x). */
const VERTICAL_TABS_KEY_STEP = 8;
const VERTICAL_TABS_KEY_STEP_BIG = 32;

interface WorkspaceProps {
  children?: ReactNode;
  className?: string;
}

type WorkspaceTabHostProps = {
  tabId: string;
  tabType: WorkspaceType;
  isActive: boolean;
};

const WorkspaceTabHost = memo(function WorkspaceTabHost({
  tabId,
  tabType,
  isActive,
}: WorkspaceTabHostProps) {
  const WorkspaceComponent = getWorkspaceComponent(tabType);

  if (!WorkspaceComponent) {
    return (
      <div className={`h-full ${isActive ? 'block' : 'hidden'}`}>
        <div className="h-full flex items-center justify-center text-muted-foreground">
          <p>Unknown workspace type: {tabType}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${isActive ? 'block' : 'hidden'}`}>
      <WorkspaceComponent tabId={tabId} />
    </div>
  );
});

export function Workspace({ children, className = '' }: WorkspaceProps) {
  // Only re-render Workspace when tab structure changes (add/remove/switch type) or activeTabId changes.
  // Tab content updates (e.g., typing in Query Editor) should not cause every tab component to re-render.
  const tabsSignature = useWorkspaceStore((state) =>
    state.tabs.filter((tab) => !tab.id.includes('::pkg::')).map((tab) => `${tab.id}::${tab.type}`).join('|')
  );
  const tabDescriptors = useMemo(() => {
    if (!tabsSignature) return [];
    return tabsSignature.split('|');
  }, [tabsSignature]);
  const activeTabId = useWorkspaceStore((state) => state.activeTabId);
  const tabOrientation = useAppStore((state) => state.tabOrientation);
  const verticalTabsWidth = useAppStore((state) => state.verticalTabsWidth);
  const setVerticalTabsWidth = useAppStore((state) => state.setVerticalTabsWidth);

  // Vertical-tabs column resize handle (CH-13): same drag/keyboard/reset
  // mechanics as the sidebar seam (MainShell) and the Scripts split
  // (SidebarContent), via the shared `useSplitterDrag` hook. Called
  // unconditionally (hooks can't be conditional) even though the handle
  // only renders in vertical tab-orientation mode.
  const { track } = useAnalytics();
  const verticalTabsColumnRef = useRef<HTMLDivElement>(null);
  const verticalTabsHandleRef = useRef<HTMLDivElement>(null);
  const verticalTabsDragStartWidthRef = useRef(verticalTabsWidth);
  const verticalTabsSplitter = useSplitterDrag({
    axis: 'x',
    onDrag: (deltaPx) => {
      const el = verticalTabsColumnRef.current;
      if (!el) return;
      const newWidth = Math.max(
        MIN_VERTICAL_TABS_WIDTH,
        Math.min(MAX_VERTICAL_TABS_WIDTH, verticalTabsDragStartWidthRef.current + deltaPx)
      );
      el.style.width = `${newWidth}px`;
      // Fix round 1, Finding 1: same live-attribute fix as the sidebar seam
      // (MainShell) — the store commit stays at drag-end to avoid a
      // re-render-per-frame storm, so `aria-valuenow` is written straight to
      // the DOM node instead of waiting for that commit.
      const handle = verticalTabsHandleRef.current;
      if (handle) {
        const rounded = Math.round(newWidth);
        handle.setAttribute('aria-valuenow', String(rounded));
        handle.setAttribute('aria-valuetext', `${rounded} pixels`);
      }
    },
    onDragEnd: () => {
      const el = verticalTabsColumnRef.current;
      if (el) setVerticalTabsWidth(el.getBoundingClientRect().width);
    },
    onReset: () => {
      setVerticalTabsWidth(DEFAULT_VERTICAL_TABS_WIDTH);
      track('feature:splitter_reset', { surface: 'vertical-tabs' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? VERTICAL_TABS_KEY_STEP_BIG : VERTICAL_TABS_KEY_STEP;
      setVerticalTabsWidth(verticalTabsWidth + direction * step);
    },
  });

  const contentArea = (
    <div className="flex-1 overflow-auto">
      <WorkspaceConnectionBanner />
      {tabDescriptors.length === 0 ? (
        children || (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <p>No active tabs. Create a new query to get started.</p>
          </div>
        )
      ) : (
        <div className="h-full">
          {tabDescriptors.map((descriptor) => {
            const delimiterIndex = descriptor.lastIndexOf('::');
            const tabId = delimiterIndex === -1 ? descriptor : descriptor.slice(0, delimiterIndex);
            const tabType = (delimiterIndex === -1 ? 'query' : descriptor.slice(delimiterIndex + 2)) as WorkspaceType;

            return (
              <WorkspaceTabHost
                key={tabId}
                tabId={tabId}
                tabType={tabType}
                isActive={tabId === activeTabId}
              />
            );
          })}
        </div>
      )}
    </div>
  );

  if (tabOrientation === 'vertical') {
    return (
      <div className={`h-full flex bg-background ${className}`} data-tab-orientation="vertical">
        <div
          ref={verticalTabsColumnRef}
          className="relative h-full flex flex-col border-r border-border"
          style={{
            width: `${verticalTabsWidth}px`,
            minWidth: `${MIN_VERTICAL_TABS_WIDTH}px`,
            maxWidth: `${MAX_VERTICAL_TABS_WIDTH}px`,
          }}
          data-testid="vertical-tabs-column"
        >
          <Tabs />
          {/* Resize handle / column seam (CH-13). Same 1px `--divider` line +
              ~6px invisible hit area as the sidebar seam (MainShell) and the
              Scripts split (SidebarContent) — this used to paint its whole
              hit area with the untokenized `bg-muted`, which is the same
              always-visible-slab anti-pattern CH-10 already fixed on the
              sidebar seam. Hover and active-drag are pinned to the same
              `--primary` tint so a hover-vs-state-class specificity fight
              can't produce a visible mismatch. */}
          <div
            ref={verticalTabsHandleRef}
            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 bg-transparent before:absolute before:inset-y-0 before:right-0 before:w-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            style={{ touchAction: 'none', userSelect: 'none' }}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize tab list"
            aria-valuenow={Math.round(verticalTabsWidth)}
            aria-valuemin={MIN_VERTICAL_TABS_WIDTH}
            aria-valuemax={MAX_VERTICAL_TABS_WIDTH}
            aria-valuetext={`${Math.round(verticalTabsWidth)} pixels`}
            tabIndex={0}
            data-dragging={verticalTabsSplitter.isDragging ? 'true' : undefined}
            data-testid="vertical-tabs-resize-handle"
            onPointerDown={(e) => {
              verticalTabsDragStartWidthRef.current = verticalTabsWidth;
              verticalTabsSplitter.onPointerDown(e);
            }}
            onDoubleClick={verticalTabsSplitter.onDoubleClick}
            onKeyDown={verticalTabsSplitter.onKeyDown}
          />
        </div>
        {contentArea}
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col bg-background ${className}`} data-tab-orientation="horizontal">
      <Tabs />
      {contentArea}
    </div>
  );
}
